import React, { useState } from 'react';
import { Box, Link, Typography } from '@mui/material';

import { checkIsPermitted, useGetUserMe } from 'src/entities/user';
import { routesMap } from 'src/routes';
import { UserRole } from 'src/shared/api';

import { CertificateGroup } from './model';

export const certificateStatusColor = (
  status: string,
): 'default' | 'success' | 'error' | 'warning' | 'info' => {
  switch (status) {
    case 'valid':
      return 'success';
    case 'expired':
    case 'suspended':
    case 'not_found':
    case 'replaced':
      return 'error';
    case 'unverifiable':
    case 'unknown':
      return 'warning';
    default:
      return 'default';
  }
};

const VISIBLE_PRODUCT_CODES = 5;

interface ProductCodesCellProps {
  certificate: CertificateGroup;
  /** false — показать все товары документа (страница документа). */
  collapsible?: boolean;
}

/**
 * Товары документа: количество и коды. Код ведёт в карточку товара только для
 * ROLE_ADMIN: маршрут `/products/:productCode` доступен только админам, а
 * список сертификатов — и менеджерам.
 */
export const ProductCodesCell: React.FunctionComponent<ProductCodesCellProps> = ({
  certificate,
  collapsible = true,
}) => {
  const [expanded, setExpanded] = useState(false);
  const userRequest = useGetUserMe();
  const canOpenProduct = checkIsPermitted(
    [UserRole.ROLE_ADMIN],
    userRequest.data?.roles,
  );

  const codes = certificate.products || [];
  const hiddenCount = collapsible
    ? Math.max(0, codes.length - VISIBLE_PRODUCT_CODES)
    : 0;
  const visibleCodes = expanded ? codes : codes.slice(0, VISIBLE_PRODUCT_CODES);

  if (codes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}
    >
      <Typography variant="body2" color="text.secondary">
        {certificate.productCount ?? codes.length}:
      </Typography>
      {visibleCodes.map(code =>
        canOpenProduct ? (
          <Link
            key={code}
            href={routesMap.product.getRoute(code)}
            underline="hover"
            variant="body2"
          >
            {code}
          </Link>
        ) : (
          <Typography key={code} variant="body2">
            {code}
          </Typography>
        ),
      )}
      {hiddenCount > 0 && (
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setExpanded(current => !current)}
        >
          {expanded ? 'свернуть' : `+${hiddenCount}`}
        </Link>
      )}
    </Box>
  );
};
